// SPDX-License-Identifier: AGPL-3.0-or-later
import styles from '@app/features/konvo_assistant/components/AssistantSpeechControls.module.css';
import AssistantSpeech from '@app/features/konvo_assistant/state/AssistantSpeech';
import {isKonvoVoice, KONVO_VOICES} from '@app/features/konvo_assistant/state/KonvoVoicePlayer';
import VoiceTts, {type TtsVoiceOption} from '@app/features/voice/utils/VoiceTtsUtils';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useEffect, useId, useState} from 'react';

export const AssistantSpeechPreferences = observer(() => {
	const {i18n} = useLingui();
	const id = useId();
	const [voices, setVoices] = useState<Array<TtsVoiceOption>>([]);
	useEffect(() => {
		let disposed = false;
		let sequence = 0;
		const refresh = () => {
			const request = ++sequence;
			void VoiceTts.getVoiceOptions().then((options) => {
				if (!disposed && request === sequence) setVoices(options);
			});
		};
		refresh();
		window.speechSynthesis?.addEventListener('voiceschanged', refresh);
		return () => {
			disposed = true;
			window.speechSynthesis?.removeEventListener('voiceschanged', refresh);
		};
	}, []);
	const voiceName = (voice: TtsVoiceOption) => {
		if (!voice.builtin) return `${voice.name} · ${voice.language}`;
		try {
			return new Intl.DisplayNames([i18n.locale], {type: 'language'}).of(voice.language) ?? voice.name;
		} catch {
			return voice.name;
		}
	};
	return (
		<div className={styles.preferences} data-flx="konvo-assistant.speech-preferences">
			<label htmlFor={`${id}-rate`}>
				<Trans>Reading speed</Trans>
			</label>
			<select
				id={`${id}-rate`}
				className={styles.select}
				value={AssistantSpeech.rate}
				onChange={(event) => AssistantSpeech.setRate(Number(event.target.value))}
			>
				{[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2].map((rate) => (
					<option key={rate} value={rate}>
						{rate}×
					</option>
				))}
			</select>
			<label htmlFor={`${id}-voice`}>
				<Trans>Voice</Trans>
			</label>
			<select
				id={`${id}-voice`}
				className={styles.select}
				value={AssistantSpeech.voiceId}
				onChange={(event) => AssistantSpeech.setVoice(event.target.value)}
			>
				<option value="">{i18n._(msg`Automatic (Heart for English)`)}</option>
				{AssistantSpeech.voiceId &&
					!isKonvoVoice(AssistantSpeech.voiceId) &&
					!voices.some((voice) => voice.id === AssistantSpeech.voiceId) && (
						<option value={AssistantSpeech.voiceId}>{i18n._(msg`Saved voice unavailable — using automatic`)}</option>
					)}
				<optgroup label={i18n._(msg`Konvo voices (English)`)}>
					{KONVO_VOICES.map((voice) => (
						<option key={voice.id} value={voice.id}>
							{voice.name}
						</option>
					))}
				</optgroup>
				<optgroup label={i18n._(msg`Device voices`)}>
					{voices
						.filter((voice) => !voice.builtin)
						.map((voice) => (
							<option key={voice.id} value={voice.id}>
								{voiceName(voice)}
							</option>
						))}
				</optgroup>
				<optgroup label={i18n._(msg`Built-in voices`)}>
					{voices
						.filter((voice) => voice.builtin)
						.map((voice) => (
							<option key={voice.id} value={voice.id}>
								{voiceName(voice)}
							</option>
						))}
				</optgroup>
			</select>
			<p className={styles.hint}>
				<Trans>Applies to the next reply or replay. Saved on this device.</Trans>
			</p>
			<button
				type="button"
				className={styles.button}
				onClick={() =>
					AssistantSpeech.read(
						'assistant-voice-preview',
						i18n._(msg`Hi, I'm Konvo. This is how I'll read your replies.`),
					)
				}
			>
				{AssistantSpeech.currentId === 'assistant-voice-preview' ? (
					<Trans>Stop preview</Trans>
				) : (
					<Trans>Preview voice</Trans>
				)}
			</button>
		</div>
	);
});
